import { useCallback, useEffect, useRef, useState } from 'react';
import { playbackUrlFor, trackKey } from '../../services/studentTracksModel';
import { createKeyShifter, keyShiftSupported, clampSemitones } from './keyShift';

/**
 * ONE open track per page, owned by the page — the RecapDrawer lesson
 * (2026-09-13) applied to sound. Every track pill on the page, whichever song
 * tab or repertoire row it sits in, opens into this single slot, so two cuts
 * can never play over each other and a pill always knows whether it is the
 * one that is open.
 *
 * TWO ENGINES, ONE SLOT (2026-09-16, after two live failures in one evening).
 * A file the filing routine has copied into Firebase Storage plays in our
 * own <audio> element — one tap, our own transport, served from our project
 * with byte ranges and no Google session involved. A file with no Storage
 * copy yet opens Drive's embedded player instead (TrackPills mounts the
 * iframe). What is NOT here, and must not return: streaming the Drive
 * download host into <audio> — Drive answers 403 to any request carrying
 * `Sec-Fetch-Site: cross-site`, i.e. every cross-origin <audio src>, while
 * curl (no Sec-Fetch headers) gets 200; and relying on the embed alone,
 * which needs the viewer's Google session to be clean — a stale one gets a
 * 401 from Drive's stream endpoint and a player stuck at 0:00 / 0:00.
 *
 * The element is a bare `new Audio()` rather than JSX: nothing about it needs
 * to be in the tree, and an imperative element survives whichever column or
 * sheet the pills happen to render in at a given width.
 */
const formatTime = (s) => {
	if (!Number.isFinite(s) || s < 0) return '0:00';
	const m = Math.floor(s / 60);
	const r = Math.floor(s % 60);
	return `${m}:${String(r).padStart(2, '0')}`;
};

const PLAY_FAILED = 'This track would not play here — open it in Drive.';

const useTrackPlayer = () => {
	const audioRef = useRef(null);
	const [current, setCurrent] = useState(null); // { key, file, engine: 'audio' | 'embed' }
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [error, setError] = useState('');
	// Key change (2026-09-26, see keyShift.js). Remembered per track for the
	// page's life, so switching cuts and coming back keeps the student's key.
	const [semitones, setSemitonesState] = useState(0);
	const [keyAvailable, setKeyAvailable] = useState(() => keyShiftSupported());
	const shifterRef = useRef(null);
	const keysRef = useRef({});
	const corsFallbackRef = useRef(false);
	// Rehearsal mode (2026-09-28). Anthony: "options for looping sections and
	// dropping the vocals in and out with a button click, also maybe a volume
	// slider on the vocals. Sometimes they like to have the vocals on a lower
	// level." A LOOP ({ start, end, sectionId }) holds the playhead inside one
	// section; VOCALS play the kept Vocals stem in a second, hidden element,
	// held to the Track's clock, at a level the teacher sets. Both live here
	// because the player owns the clock they follow.
	const [loop, setLoopState] = useState(null);
	const loopRef = useRef(null);
	const [vocals, setVocalsState] = useState(null); // { key, file, on, level }
	const vocRef = useRef(null);
	const vocHandleRef = useRef(null);
	const vocActiveRef = useRef(false);
	const pendingSeekRef = useRef(null); // { t, play } applied once a swapped file loads
	// Trim (2026-09-28): start / end points on the open file, no new file.
	// Playback starts at `start` and stops at `end` (null = the file's end).
	// Set by the page from the song's stored trim; a loop inside it wins.
	const [bounds, setBoundsState] = useState(null);
	const boundsRef = useRef(null);

	const audio = () => {
		if (!audioRef.current && typeof Audio !== 'undefined') {
			const el = new Audio();
			el.preload = 'none';
			// Required for Web Audio to hear a cross-origin file at all (a
			// non-CORS element routed into a graph outputs silence, no error).
			// If the bucket ever stops sending the header, onError below
			// reloads without it and turns key change off.
			if (keyShiftSupported()) el.crossOrigin = 'anonymous';
			audioRef.current = el;
		}
		return audioRef.current;
	};

	useEffect(() => {
		const el = audio();
		if (!el) return undefined;
		const voc = () => (vocActiveRef.current ? vocRef.current : null);
		// Hold the vocals to the Track's clock: a drift past 80 ms is pulled back.
		const syncVoc = (force = false) => {
			const v = voc();
			if (!v) return;
			if (force || Math.abs((v.currentTime || 0) - (el.currentTime || 0)) > 0.08) {
				try {
					v.currentTime = el.currentTime || 0;
				} catch (e) {
					/* before metadata */
				}
			}
		};
		const onTime = () => {
			const l = loopRef.current;
			const b = boundsRef.current;
			if (l && el.currentTime >= l.end) el.currentTime = l.start;
			else if (!l && b && b.end !== null && el.currentTime >= b.end) {
				// The trimmed end: stop, and be ready at the trimmed start.
				el.pause();
				el.currentTime = b.start;
			}
			setCurrentTime(el.currentTime || 0);
			syncVoc();
		};
		const onDur = () => setDuration(Number.isFinite(el.duration) ? el.duration : 0);
		const onMeta = () => {
			onDur();
			// A Reference -> Track swap resumes at the same moment (same timeline).
			const p = pendingSeekRef.current;
			if (p) {
				pendingSeekRef.current = null;
				try {
					el.currentTime = p.t;
				} catch (e) {
					/* ignore */
				}
				if (p.play) safePlay(el);
			}
		};
		const onPlay = () => {
			const b = boundsRef.current;
			if (b && (el.currentTime < b.start - 0.05 || (b.end !== null && el.currentTime >= b.end))) el.currentTime = b.start;
			setIsPlaying(true);
			const v = voc();
			if (v) {
				syncVoc(true);
				v.play().catch(() => {});
			}
		};
		const onPause = () => {
			setIsPlaying(false);
			const v = voc();
			if (v) v.pause();
		};
		const onSeeked = () => syncVoc(true);
		const onEnded = () => {
			// A loop that runs to the end of the file goes round again.
			const l = loopRef.current;
			if (l) {
				el.currentTime = l.start;
				safePlay(el);
				return;
			}
			setIsPlaying(false);
			setCurrentTime(0);
			const v = voc();
			if (v) v.pause();
		};
		const onError = () => {
			// A CORS refusal looks like any load error. Retry ONCE without
			// crossOrigin, before the key graph exists (it is only built on a
			// key tap, after a successful load), so the track still plays.
			if (el.crossOrigin && !corsFallbackRef.current && !shifterRef.current && el.src) {
				corsFallbackRef.current = true;
				setKeyAvailable(false);
				const src = el.src;
				el.removeAttribute('crossorigin');
				el.src = src;
				safePlay(el);
				return;
			}
			setIsPlaying(false);
			setError(PLAY_FAILED);
		};
		el.addEventListener('timeupdate', onTime);
		el.addEventListener('durationchange', onDur);
		el.addEventListener('loadedmetadata', onMeta);
		el.addEventListener('play', onPlay);
		el.addEventListener('pause', onPause);
		el.addEventListener('seeked', onSeeked);
		el.addEventListener('ended', onEnded);
		el.addEventListener('error', onError);
		return () => {
			el.removeEventListener('timeupdate', onTime);
			el.removeEventListener('durationchange', onDur);
			el.removeEventListener('loadedmetadata', onMeta);
			el.removeEventListener('play', onPlay);
			el.removeEventListener('pause', onPause);
			el.removeEventListener('seeked', onSeeked);
			el.removeEventListener('ended', onEnded);
			el.removeEventListener('error', onError);
			// Leaving the page (lesson ended, profile closed) stops the sound —
			// an unmounted hook must not keep a cut playing in the room.
			if (shifterRef.current) shifterRef.current.close();
			try {
				el.pause();
				el.removeAttribute('src');
				el.load();
				if (vocRef.current) vocRef.current.pause();
			} catch (e) {
				/* jsdom has no media pipeline; nothing to release */
			}
		};
	}, []);

	// Tighter than timeupdate (~4 a second): while a loop plays, check every
	// frame so the jump back lands on the beat rather than up to 250 ms late.
	useEffect(() => {
		const hasEnd = Boolean(bounds && bounds.end !== null);
		if ((!loop && !hasEnd) || !isPlaying || typeof requestAnimationFrame !== 'function') return undefined;
		let raf = 0;
		const tick = () => {
			const el = audioRef.current;
			const l = loopRef.current;
			const b = boundsRef.current;
			if (el && l && el.currentTime >= l.end - 0.03) el.currentTime = l.start;
			else if (el && !l && b && b.end !== null && el.currentTime >= b.end - 0.03) {
				el.pause();
				el.currentTime = b.start;
			}
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [loop, bounds, isPlaying]);

	/** End rehearsal: the vocals stop, the loop lets go. */
	const endRehearsal = () => {
		vocActiveRef.current = false;
		if (vocRef.current) {
			try {
				vocRef.current.pause();
			} catch (e) {
				/* jsdom */
			}
		}
		setVocalsState(null);
		loopRef.current = null;
		setLoopState(null);
	};

	const stopAudio = () => {
		const el = audioRef.current;
		endRehearsal();
		if (!el) return;
		try {
			el.pause();
		} catch (e) {
			/* jsdom */
		}
		setIsPlaying(false);
		setCurrentTime(0);
		setDuration(0);
	};

	const safePlay = (el) => {
		try {
			const p = el.play();
			if (p && typeof p.catch === 'function') {
				p.catch((err) => {
					// AbortError = a newer play() superseded this one; not a failure.
					if (err && err.name === 'AbortError') return;
					setIsPlaying(false);
					setError(PLAY_FAILED);
				});
			}
		} catch (e) {
			setError(PLAY_FAILED);
		}
	};

	/** Open this file. Storage copy → play it (a second tap pauses, a third
	 *  resumes). No Storage copy → open Drive's embed (a second tap closes it).
	 *  A tap on any other file swaps the slot either way. `opts.at` starts a
	 *  newly opened file at that second (a tapped lyric line, 2026-09-30),
	 *  applied when its metadata loads, the same path as a Reference -> Track
	 *  swap. */
	const toggle = useCallback(
		(file, opts = null) => {
			if (!file) return;
			const key = trackKey(file);
			const src = playbackUrlFor(file);
			setError('');
			if (current && current.key === key) {
				if (current.engine === 'audio') {
					const el = audio();
					if (!el) return;
					if (el.paused) safePlay(el);
					else el.pause();
				} else {
					setCurrent(null);
				}
				return;
			}
			stopAudio();
			if (src) {
				const el = audio();
				if (!el) return;
				const at = opts && Number(opts.at);
				pendingSeekRef.current = Number.isFinite(at) && at > 0 ? { t: at, play: true } : null;
				el.src = src;
				setCurrent({ key, file, engine: 'audio' });
				// This track's own key (Original unless changed this visit).
				const k = keysRef.current[key] || 0;
				setSemitonesState(k);
				if (shifterRef.current) shifterRef.current.setSemitones(k).catch(() => {});
				safePlay(el);
			} else {
				setCurrent({ key, file, engine: 'embed' });
			}
		},
		[current]
	);

	const close = useCallback(() => {
		stopAudio();
		setCurrent(null);
	}, []);

	const seek = useCallback((t) => {
		const el = audioRef.current;
		if (!el || !Number.isFinite(t)) return;
		try {
			el.currentTime = Math.max(0, t);
			setCurrentTime(el.currentTime);
		} catch (e) {
			/* before metadata, seeking throws in some engines; ignore */
		}
	}, []);

	/** Move the open track's key. Call from a tap (an AudioContext only
	 *  starts on a user gesture). */
	const setKey = useCallback(
		async (n) => {
			const el = audioRef.current;
			if (!keyAvailable || !current || current.engine !== 'audio' || !el) return;
			const next = clampSemitones(n);
			keysRef.current[current.key] = next;
			setSemitonesState(next);
			try {
				if (!shifterRef.current) shifterRef.current = createKeyShifter(el);
				await shifterRef.current.setSemitones(next);
			} catch (e) {
				console.error('Key change failed:', e);
				setKeyAvailable(false);
				setSemitonesState(0);
				setError('Key change is not available in this browser.');
			}
		},
		[keyAvailable, current]
	);

	/** Loop one stretch of the open track ({ start, end, sectionId }), or null. */
	const setLoop = useCallback((l) => {
		const next = l && Number.isFinite(l.start) && Number.isFinite(l.end) && l.end > l.start ? { ...l } : null;
		loopRef.current = next;
		setLoopState(next);
		const el = audioRef.current;
		if (next && el && (el.currentTime < next.start || el.currentTime >= next.end)) {
			try {
				el.currentTime = next.start;
				setCurrentTime(next.start);
			} catch (e) {
				/* before metadata */
			}
		}
	}, []);

	const vocalEl = () => {
		if (!vocRef.current && typeof Audio !== 'undefined') {
			const v = new Audio();
			v.preload = 'auto';
			if (keyShiftSupported() && !corsFallbackRef.current) v.crossOrigin = 'anonymous';
			vocRef.current = v;
		}
		return vocRef.current;
	};

	/**
	 * Bring the Vocals stem in or out, at a level (0..1), beside the open
	 * Track. Call from a tap. Where Web Audio is available the vocals join the
	 * key path (a key change moves the singer with the band) and the level is
	 * a gain; otherwise it is the element's own volume.
	 */
	const setVocals = useCallback(
		async ({ file, on = true, level = 1 }) => {
			const el = audioRef.current;
			const v = vocalEl();
			const src = file ? playbackUrlFor(file) : null;
			if (!el || !v || !src || !current || current.engine !== 'audio') return;
			if (v.src !== src) v.src = src;
			if (keyAvailable && !vocHandleRef.current) {
				try {
					if (!shifterRef.current) shifterRef.current = createKeyShifter(el);
					vocHandleRef.current = await shifterRef.current.attach(v);
				} catch (e) {
					console.error('Vocals could not join the key path:', e);
					vocHandleRef.current = null;
				}
			}
			const n = Math.max(0, Math.min(1, Number(level)));
			const g = on ? n : 0;
			if (vocHandleRef.current) {
				vocHandleRef.current.setGain(g);
				v.volume = 1;
			} else {
				v.volume = g;
			}
			vocActiveRef.current = true;
			try {
				v.currentTime = el.currentTime || 0;
			} catch (e) {
				/* before metadata */
			}
			if (!el.paused) v.play().catch(() => {});
			setVocalsState({ key: trackKey(file), file, on: Boolean(on), level: n });
		},
		[current, keyAvailable]
	);

	/**
	 * The open file's trim: { start, end } in seconds (end null = the file's
	 * end), or null for the whole file. Moves the playhead inside if it sits
	 * before the start.
	 */
	const setBounds = useCallback((b) => {
		const start = b ? Math.max(0, Number(b.start) || 0) : 0;
		const end = b && b.end !== null && b.end !== undefined && Number.isFinite(Number(b.end)) ? Number(b.end) : null;
		const next = b && (start > 0 || end !== null) && (end === null || end > start) ? { start, end } : null;
		const prev = boundsRef.current;
		if ((prev && next && prev.start === next.start && prev.end === next.end) || (!prev && !next)) return;
		boundsRef.current = next;
		setBoundsState(next);
		const el = audioRef.current;
		if (next && el && el.currentTime < next.start) {
			try {
				el.currentTime = next.start;
				setCurrentTime(next.start);
			} catch (e) {
				/* before metadata: onPlay moves it */
			}
		}
	}, []);

	/** Stop the vocals stem (the loop stays). */
	const clearVocals = useCallback(() => {
		vocActiveRef.current = false;
		if (vocRef.current) vocRef.current.pause();
		setVocalsState(null);
	}, []);

	/**
	 * Swap the open file for another on the SAME timeline (Reference -> Track
	 * for rehearsal), keeping the moment, the play state, the key and the loop.
	 */
	const swapTo = useCallback(
		(file) => {
			const el = audioRef.current;
			const src = file ? playbackUrlFor(file) : null;
			if (!el || !src || !current || current.engine !== 'audio') return;
			pendingSeekRef.current = { t: el.currentTime || 0, play: !el.paused };
			const key = trackKey(file);
			keysRef.current[key] = keysRef.current[current.key] || 0;
			el.src = src;
			setCurrent({ key, file, engine: 'audio' });
		},
		[current]
	);

	const isCurrent = useCallback((file) => Boolean(current && file && current.key === trackKey(file)), [current]);
	// Lesson Dashboard pass 3 E1: the strip's player error line has a Dismiss.
	const clearError = useCallback(() => setError(''), []);

	return {
		current,
		isPlaying,
		currentTime,
		duration,
		error,
		clearError,
		toggle,
		close,
		seek,
		isCurrent,
		formatTime,
		semitones,
		keyAvailable,
		setKey,
		loop,
		setLoop,
		vocals,
		setVocals,
		clearVocals,
		swapTo,
		bounds,
		setBounds,
	};
};

export default useTrackPlayer;
